import React from 'react';
import { Product } from '../types';
import { useTheme } from '../contexts/ThemeContext';
import { useCurrency } from '../hooks/useCurrency';
import { Eye, CheckSquare, Square } from 'lucide-react';

interface ProductCardProps {
  Product: Product;
  onEdit?: (product: Product) => void;
  onDelete?: (id: number) => void;
  onSelect?: (product: Product) => void;
  onPreview?: (product: Product) => void;
  isSelected?: boolean;
  compact?: boolean;
  loading?: boolean;
}

const ProductCard: React.FC<ProductCardProps> = ({
  Product: product,
  onEdit,
  onDelete,
  onSelect,
  onPreview,
  isSelected = false,
  compact = false,
  loading = false
}) => {
  const { theme } = useTheme();
  const { formatCurrency } = useCurrency();

  const getStockStatusColor = (stockQuantity: number, minStockLevel: number) => {
    const stock = Number(stockQuantity);
    const minLevel = Number(minStockLevel);
    
    if (stock <= 0) return 'text-red-600 bg-red-50';
    if (stock <= minLevel) return 'text-yellow-600 bg-yellow-50';
    return 'text-green-600 bg-green-50';
  };

  const getStatusBadgeColor = (status: Product['status']) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const isExpiringSoon = (expiryDate: string) => {
    const today = new Date();
    const expiry = new Date(expiryDate);
    const daysUntilExpiry = Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 30 && daysUntilExpiry > 0;
  };

  const isExpired = (expiryDate: string) => {
    const today = new Date();
    const expiry = new Date(expiryDate);
    return expiry < today;
  };

  if (compact) {
    return (
      <div
        className={`p-3 rounded-lg border transition-all duration-200 cursor-pointer ${
          isSelected 
            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
            : theme === 'dark' 
              ? 'border-gray-700 bg-gray-800 hover:bg-gray-750' 
              : 'border-gray-200 bg-white hover:bg-gray-50'
        }`}
        onClick={() => onSelect?.(product)}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h3 className={`font-medium text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              {product.name}
            </h3>
            <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              {product.brand || 'N/A'} • {product.category}
            </p>
          </div>
          <div className="text-right">
            <p className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              {formatCurrency(product.selling_price)}
            </p>
            {(() => {
              const stockQty = Number(product.stock_quantity);
              const isOutOfStock = stockQty === 0 || stockQty < 0;
              
              if (isOutOfStock) {
                return (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-red-600 bg-red-50">
                    Out of Stock
                  </span>
                );
              } else {
                return (
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStockStatusColor(product.stock_quantity, product.minimum_stock_level)}`}>
                    {product.stock_quantity} {product.unit}
                  </span>
                );
              }
            })()}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`p-4 sm:p-6 rounded-lg border transition-all duration-200 overflow-hidden ${
      isSelected 
        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
        : theme === 'dark' 
          ? 'border-gray-700 bg-gray-800 hover:shadow-lg' 
          : 'border-gray-200 bg-white hover:shadow-lg'
    }`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-3 sm:mb-4 min-w-0">
        <div className="flex-1 min-w-0 pr-2">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <h3 className={`font-semibold text-base sm:text-lg truncate ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              {product.name}
            </h3>
            <span className={`inline-flex items-center px-2 sm:px-2.5 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${getStatusBadgeColor(product.status)}`}>
              {product.status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
            </span>
          </div>
          
          {product.brand && (
            <p className={`text-sm mb-1 truncate ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              Brand: {product.brand}
            </p>
          )}
          
          <p className={`text-sm truncate ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
            Category: {product.category}
          </p>
        </div>

        {onSelect && (
          <button
            onClick={() => onSelect(product)}
            className="flex items-center touch-manipulation flex-shrink-0"
          >
            {isSelected ? (
              <CheckSquare className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
            ) : (
              <Square className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
            )}
          </button>
        )}
      </div>

      {/* Stock Info */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
        <div>
          <p className={`text-xs font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} mb-1`}>
            Stock Quantity
          </p>
          <div className="flex items-center gap-2">
            {(() => {
              const stockQty = Number(product.stock_quantity);
              const isOutOfStock = stockQty === 0 || stockQty < 0;
              
              if (isOutOfStock) {
                return (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium text-red-600 bg-red-50">
                    Out of Stock
                  </span>
                );
              } else {
                return (
                  <>
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium ${getStockStatusColor(product.stock_quantity, product.minimum_stock_level)}`}>
                      {product.stock_quantity} {product.unit}
                    </span>
                    {product.stock_quantity <= product.minimum_stock_level && (
                      <span className="text-xs text-red-600 font-medium">Low Stock</span>
                    )}
                  </>
                );
              }
            })()}
          </div>
        </div>
        
        {product.expiry_date && (
          <div>
            <p className={`text-xs font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} mb-1`}>
              Expiry Date
            </p>
            <div className={`text-sm font-medium break-words ${
              isExpired(product.expiry_date) 
                ? 'text-red-600' 
                : isExpiringSoon(product.expiry_date) 
                  ? 'text-yellow-600' 
                  : theme === 'dark' ? 'text-gray-300' : 'text-gray-900'
            }`}>
              <div className="mb-1">
                {new Date(product.expiry_date).toLocaleDateString()}
              </div>
              {isExpired(product.expiry_date) && (
                <div className="text-xs font-bold text-red-600">(EXPIRED)</div>
              )}
              {isExpiringSoon(product.expiry_date) && !isExpired(product.expiry_date) && (
                <div className="text-xs font-bold text-yellow-600">(EXPIRING)</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Price Info */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 mb-3 sm:mb-4 text-sm">
        <div>
          <p className={`text-xs font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} mb-1`}>
            Purchase Price
          </p>
          <p className={`font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-900'}`}>
            {formatCurrency(product.purchase_price)}
          </p>
        </div>
        <div>
          <p className={`text-xs font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} mb-1`}>
            Selling Price
          </p>
          <p className={`text-base sm:text-lg font-bold ${theme === 'dark' ? 'text-gray-300' : 'text-gray-900'}`}>
            {formatCurrency(product.selling_price)}
          </p>
        </div>
        {/* Show profit margin on desktop only to save space on mobile */}
        <div className="hidden sm:block">
          <p className={`text-xs font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} mb-1`}>
            Profit Margin
          </p>
          <p className={`font-medium text-green-600`}>
            {((product.selling_price - product.purchase_price) / product.purchase_price * 100).toFixed(1)}%
          </p>
        </div>
      </div>

      {/* Description */}
      <div className="mb-3 sm:mb-4 text-sm">
        <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} line-clamp-2 break-words ${!product.description ? 'italic opacity-60' : ''}`}>
          {product.description || 'No description added'}
        </p>
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-3 pt-3 sm:pt-4 border-t border-gray-200 dark:border-gray-700">
        {/* Preview, Edit and Delete buttons */}
        {(onPreview || onEdit || onDelete) && (
          <div className="flex gap-2">
            {onPreview && (
              <button
                onClick={() => onPreview(product)}
                disabled={loading}
                className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors touch-manipulation flex items-center justify-center gap-1 ${
                  loading
                    ? 'opacity-50 cursor-not-allowed'
                    : theme === 'dark'
                      ? 'bg-gray-600 hover:bg-gray-700 text-white'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300'
                }`}
                title="View Details"
              >
                <Eye size={16} className={loading ? 'animate-pulse' : ''} />
                View
              </button>
            )}
            
            {onEdit && (
              <button
                onClick={() => onEdit(product)}
                disabled={loading}
                className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors touch-manipulation ${
                  loading
                    ? 'opacity-50 cursor-not-allowed'
                    : theme === 'dark'
                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                Edit
              </button>
            )}
            
            {onDelete && (
              <button
                onClick={() => onDelete(product.id)}
                disabled={loading}
                className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors touch-manipulation ${
                  loading
                    ? 'opacity-50 cursor-not-allowed'
                    : theme === 'dark'
                      ? 'bg-red-600 hover:bg-red-700 text-white'
                      : 'bg-red-600 hover:bg-red-700 text-white'
                }`}
              >
                Delete
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductCard;